+<template>
  <AlertDialog :open="internalOpen"  @update:open="internalOpen = $event">
    <AlertDialogContent class="tw:!max-w-md tw:gap-12">
      <AlertDialogHeader>
        <AlertDialogTitle>
          {{ $t('Assign to another model') }}
        </AlertDialogTitle>
        <AlertDialogDescription>
          <div>
            <ul class="tw:list-disc tw:list-inside tw:text-red-600">
              <li>{{ $t('Associated tests will be moved.') }}</li>
              <li>{{ $t('Linked reviews and ratings will also be transferred.') }}</li>
            </ul>
            <div class="tw:mt-4">
              <label for="assign-to-model-id" class="tw:block tw:text-sm tw:font-medium tw:mb-2 tw:text-black">
                {{ $t('Select a model') }}
              </label>
              <Combobox
                id="assign-to-model-id"
                :options="options"
                :fullwidth="true"
                v-model="selectedProductId"
              ></Combobox>
            </div>
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel asChild>
          <Button variant="secondary" @click="internalOpen = false" :disabled="isLoading">{{ $t('Cancel') }}</Button>
        </AlertDialogCancel>
        <Button
          variant="destructive"
          :disabled="!selectedProductId || isLoading"
          @click.prevent="handleSave"
          :loading="isLoading"
        >
          {{ $t('Assign to another model') }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shadcn/components/ui/alert-dialog";
import { Button } from "@/shadcn/components/ui/button";
import Combobox from "@/components/ui/Combobox.vue";
import { toast } from "vue-sonner";

interface Props {
  options?: any[]
  equipmentIds?: number[]
  action: string
  open?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  options: () => [],
  equipmentIds: () => [],
  open: false
})

const emit = defineEmits(['update:open', 'success'])

const selectedProductId = ref<string | null>(null)
const isLoading = ref(false)
const internalOpen = ref(props.open)

// Watch for changes to the open prop
watch(() => props.open, (newVal) => {
  internalOpen.value = newVal
})

// Watch for changes to internalOpen and emit update
watch(internalOpen, (newVal) => {
  emit('update:open', newVal)
})

const csrf_token = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

const isBulkOperation = computed(() => {
  return props.equipmentIds && props.equipmentIds.length > 0
})

const handleSave = async () => {
  if (!selectedProductId.value) return

  isLoading.value = true

  try {
    let response;
    
    if (isBulkOperation.value) {
      // Bulk operation using bulk action API
      response = await fetch(props.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
        },
        body: JSON.stringify({
          action: 'assign-to-another-product',
          recordIds: props.equipmentIds,
          product_id: selectedProductId.value
        })
      })
    } else {
      // Single equipment operation (existing behavior)
      response = await fetch(props.action, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrf_token,
        },
        body: JSON.stringify({
          product_id: selectedProductId.value
        })
      })
    }

    const data = await response.json()

    if (!response.ok && !data.errors?.length) {
      throw new Error('Failed to update equipment product')
    }

    // Reset form and close dialog
    selectedProductId.value = null
    internalOpen.value = false

    // Show success message
    if (isBulkOperation.value) {
      if (data.processed && data.processed.length > 0) {
        data.processed.forEach(msg => toast.success(msg))
      }
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach(error => toast.error(error))
      }
      emit('success')
    } else {
      // Handle single equipment success
      if (data.redirect) {
        window.location.href = data.redirect
      }
    }

  } catch (error) {
    console.error(error.message)
    toast.error(error.message)
  } finally {
    isLoading.value = false
  }
}
</script>
